// Részleg (department) create/edit — jobb oldali beúszó FormSheet (a service-catalog/assets szerkesztő-minta).
// A szervezeti fa LEGFELSŐ szintje. Egy komponens KÉT módban: `department == null` → LÉTREHOZÁS (POST),
// különben SZERKESZTÉS (PATCH If-Match/OCC). A név/leírás KÉTNYELVŰ (nameHu/nameEn + descriptionHu/En).
// A `slug` GENERÁLT (nem form-mező; a szerver a nameEn-ből).
//
// SZÍN-VÁLASZTÓ ITT NINCS: a szín a CSAPAT szintjén él (team-form-sheet.tsx), hogy a rendszerben EGY
// szervezeti szín-forrás legyen. A részleg-sor a csapatai színeit mutatja, sajátja nincs.
// A csapatokat NEM itt rendeljük a részleghez, hanem a csapat űrlapján (a gyerek mondja meg a szülőt —
// így egy csapatnak mindig pontosan egy részlege van, és nincs két hely, ahol elállítható).
//
// A validálás forrás-igazsága a @atlas/contracts DepartmentWriteBodySchema (a features/org-structure/form.ts
// resolver-én át); a wire-body-t a `toDepartmentBody` normalizálja.
import { type OrgDepartmentRowSchema } from '@atlas/contracts';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { BilingualField } from '@/components/shared/bilingual-field';
import { FormSheet } from '@/components/shared/form-sheet';
import { SlugHint } from '@/components/shared/slug-hint';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { departmentKeys, positionKeys, teamKeys } from '@/features/org-structure/api';
import {
  buildDepartmentResolver,
  EMPTY_DEPARTMENT_FORM,
  toDepartmentBody,
  type DepartmentFormValues,
} from '@/features/org-structure/form';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';

type DepartmentRow = z.infer<typeof OrgDepartmentRowSchema>;

export interface DepartmentFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → LÉTREHOZÁS; egyébként a szerkesztendő részleg (hordozza az OCC `version`-t + az előtöltést). */
  department: DepartmentRow | null;
}

export function DepartmentFormSheet({
  open,
  onOpenChange,
  department,
}: DepartmentFormSheetProps): JSX.Element {
  const { t } = useTranslation();
  const isEdit = department !== null;

  const form = useForm<DepartmentFormValues>({
    resolver: buildDepartmentResolver(t),
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: EMPTY_DEPARTMENT_FORM,
  });
  const { reset, handleSubmit, setError } = form;

  useEffect(() => {
    if (department) {
      reset({
        nameHu: department.nameHu,
        nameEn: department.nameEn,
        descriptionHu: department.descriptionHu ?? '',
        descriptionEn: department.descriptionEn ?? '',
      });
    } else {
      reset(EMPTY_DEPARTMENT_FORM);
    }
  }, [department, reset]);

  const mutation = useEntityMutation<DepartmentFormValues>({
    mutationFn: (values) => {
      const body = toDepartmentBody(values);
      return isEdit
        ? api.departmentUpdate.mutation({
            params: { id: department.id },
            body,
            headers: { 'if-match': String(department.version) },
          })
        : api.departmentCreate.mutation({ body });
    },
    // A részleg-név szerver-oldalon feloldott a CSAPAT-sorokon (departmentName) ÉS — két szinten át — a
    // beosztás-sorokon is → rename után mindkét lista frissüljön (különben elavult nevet mutat refetch-ig).
    invalidateKeys: [
      departmentKeys.lists(),
      departmentKeys.refs(),
      teamKeys.lists(),
      positionKeys.lists(),
    ],
    onFieldErrors: (fieldName, messages) => {
      if (fieldName in EMPTY_DEPARTMENT_FORM) {
        setError(fieldName as keyof DepartmentFormValues, { message: messages.join(' ') });
      }
    },
    messages: {
      success: isEdit
        ? t('toast:org_structure.departments.updated')
        : t('toast:org_structure.departments.created'),
      conflict: t('toast:org_structure.departments.conflict'),
      forbidden: t('toast:org_structure.departments.forbidden'),
      validation: t('toast:org_structure.departments.validation'),
      error: t('toast:org_structure.departments.error'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, { onSuccess: () => onOpenChange(false) });
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset(EMPTY_DEPARTMENT_FORM);
        onOpenChange(o);
      }}
      title={
        isEdit ? t('orgStructure.departments.editTitle') : t('orgStructure.departments.createTitle')
      }
      description={
        isEdit ? <SlugHint slug={department.slug} /> : t('orgStructure.departments.createDesc')
      }
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('orgStructure.cancel')}
          </Button>
          <Button type="submit" variant="brand" disabled={mutation.isPending}>
            {mutation.isPending ? t('orgStructure.saving') : t('orgStructure.save')}
          </Button>
        </>
      }
    >
      <Form {...form}>
        <BilingualField<DepartmentFormValues>
          variant="input"
          label={t('orgStructure.fields.name')}
          nameHu="nameHu"
          nameEn="nameEn"
          required
        />
        <BilingualField<DepartmentFormValues>
          variant="textarea"
          label={t('orgStructure.fields.description')}
          nameHu="descriptionHu"
          nameEn="descriptionEn"
          rows={2}
        />
      </Form>
    </FormSheet>
  );
}
